import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { getDocuments } from '../services/database';
import { Button } from '../components/common/Button';
import { Card, CardHeader, CardBody } from '../components/common/Card';
import {
  BarChart3,
  Printer,
  FileDown,
  FileSpreadsheet,
  ShoppingCart,
  DollarSign,
  Receipt,
  TrendingUp,
  TrendingDown,
  Wallet
} from 'lucide-react';
import { useReactToPrint } from 'react-to-print';
import { format, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { formatCurrency } from '../utils/formatters';
import { downloadReportPDF, downloadReportExcel } from '../utils/reportGenerator';

const PageContainer = styled.div`
  padding: 24px 20px;
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 12px 10px;
  }
`;

const Header = styled.div`
  position: relative;
  padding: 22px 24px;
  margin-bottom: 18px;
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.06) 0%, rgba(236, 72, 153, 0.08) 100%);
  border: 1px solid rgba(168, 85, 247, 0.22);
  border-radius: 20px;
  overflow: hidden;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.8) inset,
    0 6px 20px -8px rgba(168, 85, 247, 0.2);

  &::before {
    content: '';
    position: absolute;
    top: -40%;
    right: -10%;
    width: 220px;
    height: 220px;
    background: radial-gradient(circle, rgba(168, 85, 247, 0.22) 0%, transparent 65%);
    pointer-events: none;
  }

  h1 {
    position: relative;
    margin: 0;
    font-size: 2rem;
    font-weight: 800;
    letter-spacing: -1px;
    background: linear-gradient(135deg, #6B21A8 0%, #EC4899 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  p {
    position: relative;
    margin: 4px 0 0;
    font-size: 13.5px;
    color: #64748b;
    font-weight: 500;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 16px 14px;
    border-radius: 16px;

    h1 {
      font-size: 1.4rem;
      letter-spacing: -0.5px;
    }
  }
`;

const FilterSection = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 18px;
  flex-wrap: wrap;
  align-items: center;
  padding: 14px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.96) 0%, rgba(246, 250, 253, 0.92) 100%);
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 16px;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.9) inset,
    0 4px 14px -6px rgba(46, 91, 136, 0.08);

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    gap: 8px;
    padding: 10px;
    border-radius: 14px;
  }
`;

const ReportCard = styled.div`
  position: relative;
  margin-bottom: 16px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(246, 250, 253, 0.95) 100%);
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 18px;
  overflow: hidden;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.9) inset,
    0 1px 2px rgba(15, 23, 42, 0.04),
    0 6px 20px -8px rgba(46, 91, 136, 0.08);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #8B5CF6 0%, #EC4899 50%, #8B5CF6 100%);
    z-index: 1;
  }

  .card-header {
    position: relative;
    padding: 18px 22px 10px;
    border: none;

    h2 {
      margin: 0;
      font-size: 17px;
      font-weight: 700;
      color: #1e293b;
      letter-spacing: -0.3px;
    }
  }

  .card-body {
    padding: 4px 22px 20px;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    border-radius: 14px;

    .card-header { padding: 14px 16px 8px; }
    .card-body { padding: 4px 16px 16px; }
    h2 { font-size: 15px; }
  }
`;

const DateInput = styled.input`
  padding: 10px 14px;
  font-size: 14px;
  border: 1px solid rgba(148, 163, 184, 0.28);
  border-radius: 10px;
  background: #ffffff;
  font-weight: 500;
  color: #1e293b;
  cursor: pointer;
  transition: border-color 160ms ease, box-shadow 160ms ease;

  &:focus {
    outline: none;
    border-color: #8B5CF6;
    box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.18);
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 8px 10px;
    font-size: 13px;
  }
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
  margin-bottom: 18px;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
    margin-bottom: 14px;
  }
`;

const StatCard = styled.div`
  position: relative;
  padding: 16px 18px;
  background: #ffffff;
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 16px;
  overflow: hidden;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.9) inset,
    0 1px 2px rgba(15, 23, 42, 0.04),
    0 6px 18px -8px rgba(46, 91, 136, 0.12);
  transition: transform 220ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 220ms ease;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: ${props => props.$accent || 'linear-gradient(180deg, #8B5CF6, #EC4899)'};
  }

  &::after {
    content: '';
    position: absolute;
    top: -30%;
    right: -15%;
    width: 140px;
    height: 140px;
    background: ${props => props.$glow || 'radial-gradient(circle, rgba(139,92,246,0.1), transparent 65%)'};
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-2px);
  }

  .icon-badge {
    width: 32px;
    height: 32px;
    border-radius: 9px;
    background: ${props => props.$accent || 'linear-gradient(135deg, #8B5CF6, #EC4899)'};
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 10px;
    box-shadow: 0 4px 10px -2px rgba(0, 0, 0, 0.12);

    svg { width: 16px; height: 16px; stroke-width: 2.3; }
  }

  h3 {
    font-size: 10.5px;
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.7px;
    margin: 0 0 6px;
  }

  .value {
    font-size: 22px;
    font-weight: 800;
    letter-spacing: -0.6px;
    background: ${props => props.$gradient || 'linear-gradient(135deg, #6B21A8, #EC4899)'};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    font-variant-numeric: tabular-nums;
    line-height: 1.1;
    margin-bottom: 2px;
  }

  .label {
    color: #94a3b8;
    font-size: 11.5px;
    font-weight: 500;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 12px 14px;
    border-radius: 14px;

    .icon-badge {
      width: 26px;
      height: 26px;
      border-radius: 7px;

      svg { width: 13px; height: 13px; }
    }

    h3 { font-size: 9.5px; letter-spacing: 0.3px; }
    .value { font-size: 16px; letter-spacing: -0.3px; }
    .label { font-size: 9.5px; }
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  border-radius: 10px;
  border: 1px solid rgba(148, 163, 184, 0.18);

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    margin: 0 -6px;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th {
    padding: 12px 14px;
    background: linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%);
    color: #ffffff;
    text-align: left;
    font-size: 11.5px;
    font-weight: 700;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    white-space: nowrap;
  }

  td {
    padding: 11px 14px;
    border-bottom: 1px solid rgba(148, 163, 184, 0.16);
    font-size: 13px;
    color: #334155;
    font-weight: 500;
  }

  tbody tr {
    background: #ffffff;
    transition: background 140ms ease;
  }

  tbody tr:hover {
    background: rgba(139, 92, 246, 0.04);
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 12px;
    min-width: 500px;

    th { padding: 8px 10px; font-size: 10px; }
    td { padding: 8px 10px; font-size: 11.5px; }
  }
`;

const PrintReport = styled.div`
  padding: 40px;
  max-width: 800px;
  margin: 0 auto;
  
  @media print {
    padding: 20px;
  }
`;

const ReportHeader = styled.div`
  text-align: center;
  margin-bottom: 40px;
  
  h1 {
    margin-bottom: 10px;
  }
  
  .date-range {
    color: ${props => props.theme.colors.text.secondary};
  }
`;

const ReportSection = styled.div`
  margin-bottom: 30px;
  
  h2 {
    margin-bottom: 15px;
    font-size: ${props => props.theme.typography.fontSize.xl};
  }
`;

const Select = styled.select`
  padding: 10px 38px 10px 14px;
  font-size: 14px;
  border: 1px solid rgba(148, 163, 184, 0.28);
  border-radius: 10px;
  background-color: #ffffff;
  color: #1e293b;
  font-weight: 600;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%238B5CF6' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 16px;
  min-width: 140px;
  transition: border-color 160ms ease, box-shadow 160ms ease;

  &:focus {
    outline: none;
    border-color: #8B5CF6;
    box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.18);
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 8px 32px 8px 10px;
    font-size: 13px;
    min-width: 100px;
  }
`;

export const Reports = () => {
  const { userId, userData } = useAuth();
  const [sales, setSales] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [period, setPeriod] = useState('daily');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const reportRef = useRef();

  useEffect(() => {
    loadSales();
    loadExpenses();
  }, [userId]);

  useEffect(() => {
    if (sales.length > 0 || expenses.length > 0) {
      generateReport();
    }
  }, [sales, expenses, period, selectedDate, userId]);

  const loadSales = async () => {
    if (!userId) return;
    
    try {
      setLoading(true);
      const salesData = await getDocuments(userId, 'sales');
      setSales(salesData);
    } catch (error) {
      console.error('Error loading sales:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadExpenses = async () => {
    if (!userId) return;
    
    try {
      const expensesData = await getDocuments(userId, 'expenses');
      setExpenses(expensesData);
    } catch (error) {
      console.error('Error loading expenses:', error);
    }
  };

  const getDateRange = () => {
    let start, end;
    
    switch (period) {
      case 'daily':
        start = startOfDay(selectedDate);
        end = endOfDay(selectedDate);
        break;
      case 'weekly':
        start = startOfWeek(selectedDate, { locale: es });
        end = endOfWeek(selectedDate, { locale: es });
        break;
      case 'monthly':
        start = startOfMonth(selectedDate);
        end = endOfMonth(selectedDate);
        break;
      default:
        start = startOfDay(selectedDate);
        end = endOfDay(selectedDate);
    }
    
    return { start, end };
  };

  const generateReport = async () => {
    const { start, end } = getDateRange();
    
    const filteredSales = sales.filter(sale => {
      const saleDate = parseISO(sale.date);
      return saleDate >= start && saleDate <= end;
    });

    // Cargar todos los productos para obtener costos reales
    let productsData = [];
    try {
      productsData = await getDocuments(userId, 'products');
    } catch (error) {
      console.error('Error loading products for cost calculation:', error);
    }

    const totalSales = filteredSales.length;
    const totalRevenue = filteredSales.reduce((sum, sale) => sum + sale.total, 0);
    const totalTax = filteredSales.reduce((sum, sale) => sum + sale.tax, 0);
    
    // Calcular ganancia real basada en costos reales
    let totalProfit = 0;
    let totalCost = 0;
    
    filteredSales.forEach(sale => {
      sale.items.forEach(item => {
        // Buscar el producto en el inventario para obtener su costo real
        const product = productsData.find(p => p.id === item.productId);
        const itemCost = product ? (product.cost || product.purchasePrice || (item.unitPrice * 0.7)) : (item.unitPrice * 0.7);
        const itemProfit = (item.unitPrice - itemCost) * item.quantity;
        
        totalCost += itemCost * item.quantity;
        totalProfit += itemProfit;
      });
    });
    
    // Calcular margen de ganancia promedio real
    const averageMargin = totalRevenue > 0 ? ((totalProfit / totalRevenue) * 100).toFixed(1) : 0;

    // Filter expenses by date range
    const filteredExpenses = expenses.filter(expense => {
      const expenseDate = parseISO(expense.date);
      return expenseDate >= start && expenseDate <= end;
    });

    const totalExpenses = filteredExpenses.reduce((sum, expense) => sum + expense.amount, 0);
    const netProfit = totalRevenue - totalExpenses;

    const productsSold = {};
    filteredSales.forEach(sale => {
      sale.items.forEach(item => {
        if (!productsSold[item.productName]) {
          productsSold[item.productName] = {
            quantity: 0,
            revenue: 0
          };
        }
        productsSold[item.productName].quantity += item.quantity;
        productsSold[item.productName].revenue += item.total;
      });
    });

    const topProducts = Object.entries(productsSold)
      .sort((a, b) => b[1].quantity - a[1].quantity)
      .slice(0, 10)
      .map(([name, data]) => ({ name, ...data }));

    setReportData({
      period,
      dateRange: { start, end },
      totalSales,
      totalRevenue,
      totalTax,
      totalProfit,
      totalExpenses,
      netProfit,
      averageMargin,
      sales: filteredSales,
      expenses: filteredExpenses,
      topProducts,
      productsData
    });
  };

  const handlePrint = () => {
    const printContent = reportRef.current;
    if (!printContent) return;
    
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Reporte ${format(new Date(), 'yyyy-MM-dd')}</title>
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              margin: 0;
              padding: 20px;
              color: #000;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin: 20px 0;
            }
            th, td {
              text-align: left;
              padding: 8px;
              border-bottom: 1px solid #ddd;
            }
            th {
              background-color: #f5f5f5;
            }
            @media print {
              body {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
            }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const handleDownloadPDF = () => {
    if (!reportData) return;
    downloadReportPDF(reportData, userData);
  };

  const handleDownloadExcel = () => {
    if (!reportData) return;
    downloadReportExcel(reportData, userData);
  };

  const formatDateRange = () => {
    if (!reportData) return '';
    
    const { start, end } = reportData.dateRange;
    
    switch (period) {
      case 'daily':
        return format(start, 'dd \'de\' MMMM \'de\' yyyy', { locale: es });
      case 'weekly':
        return `${format(start, 'dd/MM/yyyy')} - ${format(end, 'dd/MM/yyyy')}`;
      case 'monthly':
        return format(start, 'MMMM \'de\' yyyy', { locale: es });
      default:
        return '';
    }
  };

  return (
    <PageContainer>
      <Header>
        <h1>Reportes de Cierre de Caja</h1>
        <p>Analiza el desempeño de tus ventas y el estado financiero</p>
      </Header>

      <FilterSection>
        <Select value={period} onChange={(e) => setPeriod(e.target.value)}>
          <option value="daily">Diario</option>
          <option value="weekly">Semanal</option>
          <option value="monthly">Mensual</option>
        </Select>

        <DateInput
          type="date"
          value={format(selectedDate, 'yyyy-MM-dd')}
          onChange={(e) => setSelectedDate(new Date(e.target.value))}
        />

        <Button onClick={handlePrint}>
          <Printer size={15} style={{ marginRight: 5 }} />
          Imprimir
        </Button>
        <Button onClick={handleDownloadPDF} variant="outline">
          <FileDown size={15} style={{ marginRight: 5 }} />
          Descargar PDF
        </Button>
        <Button onClick={handleDownloadExcel} variant="success">
          <FileSpreadsheet size={15} style={{ marginRight: 5 }} />
          Exportar Excel
        </Button>
      </FilterSection>

      {loading ? (
        <p>Cargando datos...</p>
      ) : reportData ? (
        <>
          <StatGrid>
            <StatCard
              $accent="linear-gradient(135deg, #60A5FA, #2563EB)"
              $glow="radial-gradient(circle, rgba(37,99,235,0.12), transparent 65%)"
              $gradient="linear-gradient(135deg, #1E40AF, #2563EB)"
            >
              <div className="icon-badge"><ShoppingCart /></div>
              <h3>Ventas Totales</h3>
              <div className="value">{reportData.totalSales}</div>
              <div className="label">transacciones</div>
            </StatCard>

            <StatCard
              $accent="linear-gradient(135deg, #34D399, #059669)"
              $glow="radial-gradient(circle, rgba(5,150,105,0.14), transparent 65%)"
              $gradient="linear-gradient(135deg, #047857, #10B981)"
            >
              <div className="icon-badge"><DollarSign /></div>
              <h3>Ingresos</h3>
              <div className="value">{formatCurrency(reportData.totalRevenue)}</div>
              <div className="label">total vendido</div>
            </StatCard>

            <StatCard
              $accent="linear-gradient(135deg, #22D3EE, #0891B2)"
              $glow="radial-gradient(circle, rgba(8,145,178,0.14), transparent 65%)"
              $gradient="linear-gradient(135deg, #0E7490, #22D3EE)"
            >
              <div className="icon-badge"><Receipt /></div>
              <h3>IVA Recaudado</h3>
              <div className="value">{formatCurrency(reportData.totalTax)}</div>
              <div className="label">16% sobre ventas</div>
            </StatCard>

            <StatCard
              $accent="linear-gradient(135deg, #A855F7, #7E22CE)"
              $glow="radial-gradient(circle, rgba(168,85,247,0.14), transparent 65%)"
              $gradient="linear-gradient(135deg, #6B21A8, #A855F7)"
            >
              <div className="icon-badge"><TrendingUp /></div>
              <h3>Ganancia Estimada</h3>
              <div className="value">{formatCurrency(reportData.totalProfit)}</div>
              <div className="label">{reportData.averageMargin}% margen promedio</div>
            </StatCard>

            <StatCard
              $accent="linear-gradient(135deg, #FB7185, #E11D48)"
              $glow="radial-gradient(circle, rgba(225,29,72,0.14), transparent 65%)"
              $gradient="linear-gradient(135deg, #BE123C, #FB7185)"
            >
              <div className="icon-badge"><TrendingDown /></div>
              <h3>Gastos</h3>
              <div className="value">{formatCurrency(reportData.totalExpenses)}</div>
              <div className="label">total gastos</div>
            </StatCard>

            <StatCard
              $accent={reportData.netProfit >= 0
                ? 'linear-gradient(135deg, #34D399, #047857)'
                : 'linear-gradient(135deg, #F87171, #B91C1C)'}
              $glow={reportData.netProfit >= 0
                ? 'radial-gradient(circle, rgba(5,150,105,0.14), transparent 65%)'
                : 'radial-gradient(circle, rgba(185,28,28,0.14), transparent 65%)'}
              $gradient={reportData.netProfit >= 0
                ? 'linear-gradient(135deg, #047857, #10B981)'
                : 'linear-gradient(135deg, #B91C1C, #EF4444)'}
            >
              <div className="icon-badge"><Wallet /></div>
              <h3>Ganancia Neta</h3>
              <div className="value">{formatCurrency(reportData.netProfit)}</div>
              <div className="label">ingresos − gastos</div>
            </StatCard>
          </StatGrid>

          <ReportCard>
            <div className="card-header">
              <h2>Detalle de Ventas</h2>
            </div>
            <div className="card-body">
              <TableWrapper>
                <Table>
                  <thead>
                    <tr>
                      <th>Fecha/Hora</th>
                      <th>Ticket</th>
                      <th>Productos</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.sales.map((sale) => (
                      <tr key={sale.id}>
                        <td>{format(parseISO(sale.date), 'dd/MM/yyyy HH:mm', { locale: es })}</td>
                        <td>#{sale.saleNumber || sale.id.slice(-8)}</td>
                        <td>{sale.items.length} items</td>
                        <td>{formatCurrency(sale.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrapper>
            </div>
          </ReportCard>

          <ReportCard>
            <div className="card-header">
              <h2>Productos Más Vendidos</h2>
            </div>
            <div className="card-body">
              <TableWrapper>
                <Table>
                  <thead>
                    <tr>
                      <th>Producto</th>
                      <th>Cantidad</th>
                      <th>Ingresos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.topProducts.map((product, index) => (
                      <tr key={index}>
                        <td>{product.name}</td>
                        <td>{product.quantity}</td>
                        <td>{formatCurrency(product.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrapper>
            </div>
          </ReportCard>

          <div style={{ display: 'none' }}>
            <PrintReport ref={reportRef} style={{ backgroundColor: 'white', color: 'black' }}>
              <ReportHeader>
                <h1 style={{ color: 'black' }}>{userData?.pharmacyName || 'NomoFarma'}</h1>
                <h2 style={{ color: 'black' }}>Reporte de Cierre de Caja</h2>
                <p className="date-range" style={{ color: '#666' }}>{formatDateRange()}</p>
              </ReportHeader>

              <ReportSection>
                <h2 style={{ color: 'black' }}>Resumen General</h2>
                <Table style={{ color: 'black' }}>
                  <tbody>
                    <tr>
                      <td>Ventas Totales:</td>
                      <td style={{ textAlign: 'right' }}>{reportData.totalSales} transacciones</td>
                    </tr>
                    <tr>
                      <td>Ingresos Totales:</td>
                      <td style={{ textAlign: 'right' }}>{formatCurrency(reportData.totalRevenue)}</td>
                    </tr>
                    <tr>
                      <td>IVA Recaudado:</td>
                      <td style={{ textAlign: 'right' }}>{formatCurrency(reportData.totalTax)}</td>
                    </tr>
                    <tr>
                      <td>Ganancia Estimada:</td>
                      <td style={{ textAlign: 'right' }}>{formatCurrency(reportData.totalProfit)}</td>
                    </tr>
                    <tr>
                      <td>Gastos Totales:</td>
                      <td style={{ textAlign: 'right' }}>{formatCurrency(reportData.totalExpenses)}</td>
                    </tr>
                    <tr style={{ fontWeight: 'bold', borderTop: '2px solid #000' }}>
                      <td>Ganancia Neta:</td>
                      <td style={{ textAlign: 'right', color: reportData.netProfit >= 0 ? '#27AE60' : '#E74C3C' }}>
                        {formatCurrency(reportData.netProfit)}
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </ReportSection>

              <ReportSection>
                <h2 style={{ color: 'black' }}>Productos Más Vendidos</h2>
                <Table style={{ color: 'black' }}>
                  <thead>
                    <tr>
                      <th>Producto</th>
                      <th style={{ textAlign: 'right' }}>Cantidad</th>
                      <th style={{ textAlign: 'right' }}>Ingresos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.topProducts.map((product, index) => (
                      <tr key={index}>
                        <td>{product.name}</td>
                        <td style={{ textAlign: 'right' }}>{product.quantity}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(product.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </ReportSection>

              <ReportSection>
                <h2 style={{ color: 'black' }}>Detalle de Ventas</h2>
                <Table style={{ color: 'black' }}>
                  <thead>
                    <tr>
                      <th style={{ color: 'black' }}>Fecha/Hora</th>
                      <th style={{ color: 'black' }}>Ticket</th>
                      <th style={{ color: 'black' }}>Cliente</th>
                      <th style={{ color: 'black' }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.sales.slice(0, 20).map((sale) => (
                      <tr key={sale.id}>
                        <td style={{ color: 'black' }}>{format(parseISO(sale.date), 'dd/MM HH:mm', { locale: es })}</td>
                        <td style={{ color: 'black' }}>#{sale.saleNumber || sale.id.slice(-8)}</td>
                        <td style={{ color: 'black' }}>{sale.customerName || 'Público General'}</td>
                        <td style={{ color: 'black' }}>{formatCurrency(sale.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </ReportSection>

              <div style={{ marginTop: '50px', textAlign: 'center', fontSize: '12px', color: '#7F8C8D' }}>
                <p>Reporte generado el {format(new Date(), 'dd/MM/yyyy HH:mm', { locale: es })}</p>
                <p>NomoFarma - Sistema de Gestión de Farmacias</p>
              </div>
            </PrintReport>
          </div>
        </>
      ) : (
        <p>No hay datos para mostrar</p>
      )}
    </PageContainer>
  );
};